export type AnalyticsConsent = "granted" | "denied";

type EventParameters = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const CONSENT_KEY = "kbg-analytics-consent";
const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
let initialized = false;

function clearAnalyticsCookies(): void {
  const domains = [undefined, window.location.hostname, `.${window.location.hostname}`];
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0]?.trim();
    if (!name || (name !== "_ga" && !name.startsWith("_ga_"))) return;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ""}; SameSite=Lax`;
    });
  });
}

export function getAnalyticsConsent(): AnalyticsConsent | null {
  const value = localStorage.getItem(CONSENT_KEY);
  return value === "granted" || value === "denied" ? value : null;
}

function gtag(...args: unknown[]) {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args);
}

export function initializeAnalytics(): void {
  if (initialized || !measurementId || getAnalyticsConsent() !== "granted") return;
  initialized = true;
  window.gtag = gtag;
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("consent", "update", { analytics_storage: "granted" });
  gtag("js", new Date());
  gtag("config", measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    anonymize_ip: true,
  });

  if (!document.querySelector("script[data-kleanbg-analytics]")) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.dataset.kleanbgAnalytics = "true";
    document.head.append(script);
  }
}

export function setAnalyticsConsent(consent: AnalyticsConsent): void {
  localStorage.setItem(CONSENT_KEY, consent);
  window.dispatchEvent(new CustomEvent("kleanbg:analytics-consent", { detail: consent }));
  if (consent === "granted") initializeAnalytics();
  else {
    window.gtag?.("consent", "update", { analytics_storage: "denied" });
    clearAnalyticsCookies();
  }
}

export function track(event: string, parameters: EventParameters = {}): void {
  if (getAnalyticsConsent() !== "granted") return;
  initializeAnalytics();
  window.gtag?.("event", event, parameters);
}

export function trackPage(path: string): void {
  track("page_view", { page_path: path, page_location: `${window.location.origin}${window.location.pathname}`, page_title: document.title });
}
